import {
  getCoreRowModel as defaultGetCoreRowModel,
  getSortedRowModel as defaultGetSortedRowModel,
  RowModel,
  Table,
  TableOptions,
} from '@tanstack/react-table';
import { ErrorBoundary } from 'react-error-boundary';

import Body from './body';
import Header from './header';
import Pagination from './pagination';
import { TableStateProvider, useTable } from './table-state-provider';
import { FormHelperText } from '../form-helper-text/form-helper-text';

const tableClasses = 'w-full border-separate whitespace-nowrap border-spacing-0';
const wrapperClasses = 'flex-1 overflow-x-auto';
const containerClasses = 'flex flex-col overflow-scroll';

export interface ColumnMeta {
  classes: string;
}

interface TableContentProps {
  height?: number | string;
  enablePagination?: boolean;
}

export const Fallback = () => (
  <div className="flex min-h-screen flex-row items-center justify-center">
    <FormHelperText type="error" display={'Something went wrong'} visible={true} />
  </div>
);

function TableContent(props: Readonly<TableContentProps>) {
  const { height, enablePagination } = props;
  const { tableContainerRef } = useTable();
  return (
    <div className={containerClasses} style={{ height: height }}>
      <div ref={tableContainerRef} className={wrapperClasses}>
        <table className={tableClasses}>
          <ErrorBoundary fallbackRender={Fallback}>
            <Header />
            <Body />
          </ErrorBoundary>
        </table>
      </div>
      {enablePagination && (
        <div>
          <Pagination />
        </div>
      )}
    </div>
  );
}

export interface TableProps<TData> extends Omit<TableOptions<TData>, 'getCoreRowModel' | 'getSortedRowModel'> {
  height?: number | string;
  /** (DEPRECATED: use state.columnPinning instead) A list of column ids to remain in a fixed
   * position of while scrolling horizontally. */
  stickyColumns?: string[];
  /** Allows for columns to be rearranged */
  enableColumnOrdering?: boolean;
  enablePagination?: boolean;
  totalRowCount?: number;
  /** This sets the loading state of the table */
  isLoading?: boolean;
  /** This enables/disables row virtualization and also allows setting a minimum row count for virtualization. */
  enableRowVirtualization?: { minRows: number } | boolean;
  estimateRowHeight?: (index: number) => number;
  onRowSelect?: (rows: TData[]) => void;
  onRowDeselect?: (rows: TData[]) => void;
  groupBy?: string;
  hideGroupByColumn?: boolean;
  getCoreRowModel?: (table: Table<TData>) => () => RowModel<TData>;
  getSortedRowModel?: (table: Table<TData>) => () => RowModel<TData>;
}

export function TableV2<TData>({
  stickyColumns,
  enableColumnOrdering,
  totalRowCount,
  isLoading,
  enableRowVirtualization = { minRows: 200 },
  estimateRowHeight,
  onRowSelect,
  onRowDeselect,
  groupBy,
  hideGroupByColumn,
  getCoreRowModel = defaultGetCoreRowModel(),
  getSortedRowModel = defaultGetSortedRowModel(),
  ...tableOptions
}: Readonly<TableProps<TData>>) {
  return (
    <TableStateProvider<TData>
      stickyColumns={stickyColumns}
      options={{
        ...tableOptions,
        getCoreRowModel,
        getSortedRowModel,
      }}
      enableColumnOrdering={enableColumnOrdering}
      totalRowCount={totalRowCount}
      isLoading={isLoading}
      enableRowVirtualization={enableRowVirtualization}
      estimateRowHeight={estimateRowHeight}
      onRowSelect={onRowSelect}
      onRowDeselect={onRowDeselect}
      groupBy={groupBy}
      hideGroupByColumn={hideGroupByColumn}
    >
      <TableContent enablePagination={tableOptions.enablePagination} height={tableOptions.height} />
    </TableStateProvider>
  );
}
